import { useAddAnalysisToDashboard } from '@modules/analysis/hooks/useCreateAnalysis/useAddAnalysisToDashboard';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Direction } from '@modules/common/types';
import { useFetchDashboardVersion } from '@modules/dashboard/hooks';
import { EditableDashboardListItem } from '@modules/dashboard/types';
import {
  AnalysisMetadataInput,
  AnalysisUseCase,
  CreateAnalysisMutationOptions,
  GetDashboardByTokenDocument,
  ListAnalysesDocument,
  useCreateAnalysisMutation,
} from 'generated/graphql';
import { DocumentNode } from 'graphql';
import { useCallback, useEffect } from 'react';
import { Scope } from 'v2/core/entities/scope';
import { AndOfErrors } from 'v2/core/errors/errors';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { EQLProps } from 'v2/core/repository/eql/types';
import useGetAnalysisDeps from 'v2/core/services/analysis/useGetAnalysisDeps';

const CREATE_ANALYSIS_REFETCH_QUERIES = [ListAnalysesDocument];

interface CreateAnalysisOptions {
  name: string;
  scope: Scope;
  panels?: [];
  metadata?: string;
  metadataV2?: AnalysisMetadataInput;
  useCase: AnalysisUseCase;
  eqlProps: Omit<EQLProps, 'sourceDetailsMap'>;
  dashboardToAddTo?: Pick<
    EditableDashboardListItem,
    'id' | 'viewToken' | 'editToken' | 'title' | 'metadataV2'
  >;
  dashboardLayoutRowID?: string | null;
  cellInsertDirection?: Direction;
  refetchQueries?: DocumentNode[];
  description?: string;
}

export const useCreateAnalysis = ({
  name,
  scope,
  panels = [],
  metadata = '',
  metadataV2,
  useCase,
  eqlProps,
  dashboardToAddTo,
  dashboardLayoutRowID,
  cellInsertDirection = Direction.Left,
  refetchQueries,
  description,
}: CreateAnalysisOptions) => {
  const {
    data: { user, tenantID, sourceDetailsMap },
    error: dependencyError,
  } = useGetAnalysisDeps();
  const [addAnalysisToDashboardIfRequired] = useAddAnalysisToDashboard();
  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const { getVersion } = useFetchDashboardVersion();

  const [
    createAnalysisMutation,
    { loading, error: createAnalysisMutationError, called },
  ] = useCreateAnalysisMutation({
    variables: {
      tenantID,
      title: name,
      scope: mapToScope(scope),
      panels,
      description,
      // Passing metadataV2 will overwrite metadata so only pass when metadataV2 in use
      metadata,
      metadataV2: metadataV2 ? metadataV2 : undefined,
      analysisExpresssion: eql ?? '',
      createdBy: user.id ?? '',
      useCase,
      dashboardToAddTo: dashboardToAddTo?.id
        ? {
            id: dashboardToAddTo.id,
            editToken: dashboardToAddTo.editToken,
          }
        : undefined,
    },
    refetchQueries: [
      ...CREATE_ANALYSIS_REFETCH_QUERIES,
      ...(refetchQueries ?? []),
      ...(dashboardToAddTo?.viewToken
        ? [
            {
              query: GetDashboardByTokenDocument,
              variables: {
                token: dashboardToAddTo.viewToken,
                tenantID,
                userID: user.id,
                includeScheduleOutbounds: true,
              },
            },
          ]
        : []),
    ],
    awaitRefetchQueries: Boolean(dashboardToAddTo),
  });

  const status = apolloStatusToQueryStatus(
    loading,
    called,
    false,
    createAnalysisMutationError,
  );

  const error = AndOfErrors([dependencyError, createAnalysisMutationError]);

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Something went wrong.`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const createAnalysis = useCallback(
    async (args: CreateAnalysisMutationOptions) => {
      if (!sourceDetailsMap) {
        return async () => {};
      }

      const resp = await createAnalysisMutation(args);

      const createDashboardResponse =
        resp.data?.createAnalysis?.createDashboardResponse;

      const dashboard = createDashboardResponse ?? dashboardToAddTo;

      let dashboardVersion;

      if (!createDashboardResponse && dashboardToAddTo) {
        const dashboardVersionResp = await getVersion({
          token: dashboardToAddTo?.editToken,
        });
        dashboardVersion =
          dashboardVersionResp?.data?.getDashboardByToken?.version;
      }

      if (dashboard && resp.data?.createAnalysis?.id) {
        await addAnalysisToDashboardIfRequired({
          dashboard: {
            ...dashboard,
            version:
              dashboardVersion ?? createDashboardResponse?.dashboard.version,
          },
          dashboardLayoutRowID,
          cellInsertDirection,
          analysisID: resp.data?.createAnalysis?.id,
          dashboardLayout: dashboardToAddTo?.metadataV2?.layout ?? { rows: [] },
          analysesMetadata: dashboardToAddTo?.metadataV2?.analysesMetadata,
          shouldAddAnalysisInUpdateCall: false,
        });
      }

      return resp;
    },
    [
      cellInsertDirection,
      sourceDetailsMap,
      createAnalysisMutation,
      dashboardToAddTo,
      dashboardLayoutRowID,
      addAnalysisToDashboardIfRequired,
      getVersion,
    ],
  );

  return {
    isLoading: IsLoading(status),
    createAnalysis,
    error,
  };
};
